import { DeterministicView, helpers } from '@bingads-webui/deterministic-view';
import { ValidationErrorView } from '@bingads-webui/validation-error';
import _ from 'underscore';
import Backbone from 'backbone';
import template from './input-control-template.pug';

const { ensureChildView } = helpers;

/**
 * Shared control for backbone model based input control
 * @extends Backbone.View
 * @param {Object} entity - backbone model which is has binding field
 * @param {string} field - name of field from entity which is bound to input control
 * @param {string} addonText - addon text to display behind input, such as currency
 * @param {Object} template - custom pug template for input control
 * @param {boolean} showValidation - if input control shows validation error for user input
 * @param {boolean} readonly - if input control is readonly
 * @param {boolean} hideErrorOnInit - if input control shows validation error when initialized
 * @param {string} required - the filed is required or not
 * @param {string} ariaLabel - aria label of input form
 * @param {string} ariaLabelledby - aria labelledby of input form
 */
export class InputControl extends DeterministicView {
  initialize({
    entity,
    field,
    addonText,
    template: customTemplate = template,
    showValidation = true,
    readonly = false,
    hideErrorOnInit = true,
    focusId,
    required = false,
    ariaLabel = undefined,
    ariaLabelledby = undefined,
  } = {}) {
    if (!(entity instanceof Backbone.Model)) {
      throw new Error('entity should be a backbone model');
    }

    if (_.isEmpty(field)) {
      throw new Error('field is required');
    }

    this.field = field;
    this.entity = entity;
    this.addonText = addonText;
    this.customTemplate = customTemplate;
    this.focusId = focusId;
    this.ariaLabel = ariaLabel;
    this.ariaLabelledby = ariaLabelledby;
    this.required = required;

    this.setState({
      readonly,
      showValidation,
      showError: !hideErrorOnInit,
      value: entity.get(field),
    });

    this.delegateEvents({
      // Do not set this.model in keyup event to prevent view rerender and focus lost problem
      [`input .${field}-value`]: ({ target }) => {
        entity.set(field, target.value);
        this.trigger('input-changed');
        this.setState({ showError: true });
      },
      // Change event fires on focus lost (e.g. user clicks somewhere outside the input control),
      // so it is safe to set this.model without interrupting user action.
      [`change .${field}-value`]: ({ target }) => {
        this.setState({ value: target.value });
      },
    });
  }

  /**
  * Set state of input control view
  * @param {string} readonly - if input control is readonly
  * @param {boolean} showValidation - if input control shows validation error for user input
  * @param {boolean} showError - if input control shows validation error when initialized
  * @param {string} value - sets new value for input control
  * @returns {Object} - return current view object
  */
  setState({
    readonly,
    showValidation,
    showError,
    value,
  } = {}) {
    if (!_.isUndefined(readonly)) {
      this.model.set('readonly', readonly);
    }

    if (!_.isUndefined(showValidation)) {
      this.model.set('showValidation', showValidation);
      if (showValidation) {
        ensureChildView(this, 'validation-error', ValidationErrorView, {
          field: this.field,
          errors: this.entity.errors,
        });
      }
    }

    if (!_.isUndefined(showError)) {
      if (super.getState().showValidation) {
        this.getChild('validation-error').setState({ showError });
      }
    }

    if (!_.isUndefined(value)) {
      this.model.set('value', value);
      this.entity.set(this.field, value);
      if (this.model.get('value') === value) {
        this.render();
      }
    }

    return this;
  }

  /**
  * @typedef {Object} State
  * @property {string} readonly - if input control is readonly
  * @property {boolean} showValidation - if input control shows validation error for user input
  * @property {boolean} isValid - if user input passes validation
  * @property {string} value - the value for input control
  */
  /**
  * Get state of input control view
  * @returns {State} the state of view
  */
  getState() {
    return {
      readonly: this.model.get('readonly'),
      isValid: _.isEmpty(this.entity.errors.get(this.field)),
      value: this.entity.get(this.field),
      showValidation: this.model.get('showValidation'),
    };
  }

  get template() {
    return this.customTemplate;
  }
}
